import { normalizeBookingDateYmd } from './bookingUtils';

/** Client-side guard: drop heavy fields if a full row slips through. */
export function slimVenueBookingListRow(booking) {
  if (!booking || typeof booking !== 'object') return booking;
  if (booking.list_summary === true) return booking;

  const req = booking.requirements;
  let slimReq = undefined;
  if (req && typeof req === 'object' && !Array.isArray(req)) {
    slimReq = {};
    for (const key of ['source', 'recurring', 'recurrence', 'recurrence_label', 'add_ons_total']) {
      if (req[key] != null) slimReq[key] = req[key];
    }
    if (Array.isArray(req.packages) && req.packages.length) {
      slimReq.packages = req.packages.slice(0, 8).map((p) =>
        p && typeof p === 'object'
          ? {
              package_id: p.package_id,
              name: p.name,
              quantity: p.quantity,
              amount: p.amount
            }
          : p
      );
    }
  }

  return {
    ...booking,
    event_description: undefined,
    transaction_id: undefined,
    packages: undefined,
    owner_invoices: undefined,
    requirements: slimReq ?? req,
    list_summary: true
  };
}

export function normalizeVenueBookingsListRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(Boolean).map((row) => slimVenueBookingListRow(row));
}

export function mergeVenueBookingLists(existing, incoming) {
  const map = new Map();
  for (const row of existing || []) {
    if (row?.id) map.set(String(row.id), row);
  }
  for (const row of incoming || []) {
    if (!row?.id) continue;
    const id = String(row.id);
    const prev = map.get(id);
    if (!prev) {
      map.set(id, row);
      continue;
    }
    const prevFull = prev.list_summary !== true;
    const nextFull = row.list_summary !== true;
    if (nextFull || !prevFull) {
      map.set(id, row);
    }
  }
  return [...map.values()];
}

/** Map YYYY-MM-DD → bookings[] for O(1) day lookups. */
export function buildBookingsByDateIndex(bookings) {
  const index = new Map();
  if (!Array.isArray(bookings)) return index;
  for (const booking of bookings) {
    const ymd = normalizeBookingDateYmd(booking);
    if (!ymd) continue;
    if (!index.has(ymd)) index.set(ymd, []);
    index.get(ymd).push(booking);
  }
  return index;
}

const byDateIndexCache = new Map();

/** Cheap fingerprint — avoids rebuilding the day index on every Home remount with the same list. */
export function fingerprintVenueBookingsList(bookings) {
  if (!Array.isArray(bookings) || bookings.length === 0) return '0';
  const first = bookings[0];
  const last = bookings[bookings.length - 1];
  return [
    bookings.length,
    first?.id,
    first?.updated_at,
    last?.id,
    last?.updated_at
  ].join(':');
}

export function getBookingsByDateIndexCached(venueId, bookings) {
  const fp = fingerprintVenueBookingsList(bookings);
  const key = `${venueId}|${fp}`;
  const cached = byDateIndexCache.get(key);
  if (cached) return cached;
  const index = buildBookingsByDateIndex(bookings);
  byDateIndexCache.set(key, index);
  if (byDateIndexCache.size > 12) {
    const oldest = byDateIndexCache.keys().next().value;
    byDateIndexCache.delete(oldest);
  }
  return index;
}

export function getBookingsForDateFromIndex(index, date) {
  if (!index || !date) return [];
  const ymd =
    typeof date === 'string'
      ? date.split('T')[0]
      : normalizeBookingDateYmd({ booking_date: date });
  if (!ymd) return [];
  return index.get(ymd) || [];
}

export function formatLocalYmd(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDaysToYmd(ymd, deltaDays) {
  const [y, mo, d] = String(ymd).split('-').map(Number);
  const dt = new Date(y, mo - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  return formatLocalYmd(dt);
}

export function isVenueBookingListSummary(booking) {
  return booking?.list_summary === true;
}
